import { Component, ElementRef, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDialogCloseEvent, DocumentType, Role } from '../../../const';
import { Users } from '../../../models/user-center.model';
import { ToastrService } from 'ngx-toastr';
import { MatAccountService } from '../../mat-account/mat-account.service';
import { HttpErrorResponse } from '@angular/common/http';
import { DtoClientAccount, UserRoles } from '../../../models/mat-account.model';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { SharedService } from '../../../shared/services/shared.service';
import { ConfirmDialogComponent } from '../../mat-account/confirm-dialog/confirm-dialog.component';
import { Subscription } from 'rxjs';


@Component({
  selector: 'app-edit-account-details',
  templateUrl: './edit-account-details.component.html'
})
export class EditAccountDetailsComponent implements OnInit, OnDestroy {
  userRole: string[] = [];
  @ViewChild('editMatFileInput') editMatFileInput: ElementRef | undefined;
  matAccountUploadFileList: File[] = [];
  maxSize: number = 1 * 1024 * 1024; // 1 MB in bytes
  subscription: Subscription = new Subscription();
  accountAdminList: Users[] = [];
  selectedFileCount: number = 0;
  clientAccount: DtoClientAccount = {} as DtoClientAccount;
  matAccountForm = new FormGroup({
    clientAccountName: new FormControl('', [
      Validators.required,
      Validators.pattern('^.*[a-zA-Z]+.*$ ?'),
    ]),
    clientReference: new FormControl('', [
      Validators.required,
      Validators.pattern('^.*[a-zA-Z]+.*$ ?'),
    ]),
    numberOfClientAdmin: new FormControl(1, [Validators.required]),
    assignedAccountAdmin: new FormControl([], [
      Validators.required,
      Validators.pattern('^.*[a-zA-Z]+.*$ ?'),
    ]),
    notes: new FormControl('', [
      Validators.required,
      Validators.pattern('^.*[a-zA-Z]+.*$ ?'),
    ])
  });
  
  canEditName: boolean = false;
  canEditCode: boolean = false;

  constructor(
    public dialog: MatDialog, 
    @Inject(MAT_DIALOG_DATA) public data: any,
    private matAccountService: MatAccountService,
    private toasterService: ToastrService,
    private sharedService: SharedService,
    @Optional() public dialogRef: MatDialogRef<EditAccountDetailsComponent>
  ) {}

  ngOnInit(): void {
    this.getAccountAdminDetails();
    this.canEditName = this.data.userRole?.includes(Role.SuperAdmin)??false;
    this.canEditCode = this.canEditName;
    
    this.userRole = this.data.userRole;
    this.clientAccount = this.data.clientAccount;
    this.subscription.add(this.sharedService.doFileUpload.subscribe((data) => {
      if(data){
        this.uploadFromLocal(data, true);
      }
    }));
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  patchAccountDetail(){
    let accountAdmin: any = this.accountAdminList.find(x => x.userId == this.clientAccount.assignedAccountAdmin)!;
    this.matAccountForm.patchValue({
      clientAccountName: this.clientAccount.clientAccountName,
      clientReference: this.clientAccount.clientReference,
      numberOfClientAdmin: this.clientAccount.numberOfClientAdmin,
      notes: this.clientAccount.notes,
      assignedAccountAdmin: accountAdmin
    });
  }

  update(){
    if(this.matAccountForm.invalid){
      this.matAccountForm.markAllAsTouched();
      this.toasterService.warning("Please fill the required fields to proceed");
      return;
    }

    if(this.matAccountForm.untouched){
      this.toasterService.info("No changes to save");
      return;
    }
    
    let matAccountDetails = JSON.parse(
      JSON.stringify(this.matAccountForm.value)
    );
    const MatAccountformData = new FormData();

    this.matAccountUploadFileList.forEach((file: File) => {
      MatAccountformData.append('file', file, file.name);
    });

    matAccountDetails.clientAccountId = this.clientAccount.clientAccountId;
    matAccountDetails.assignedAccountAdmin = matAccountDetails.assignedAccountAdmin?.userId;
    
    var isConfirmed = (matAccountDetails.clientReference==this.clientAccount.clientReference);
    if(!isConfirmed){
      isConfirmed = window.confirm('Are you sure you want to change the Client Account Reference?');
    }
    if(isConfirmed)
      this.saveUpdate(matAccountDetails, MatAccountformData);
    
  }

  saveUpdate(matAccountDetails: any, MatAccountformData: FormData){
    
    this.matAccountService.editMatAccount(matAccountDetails).subscribe((response: any) => {
          if (response.isSuccess) {
            if(this.matAccountUploadFileList.length){
              let matAccountId = response.message;
              this.uploadDocument(MatAccountformData, DocumentType.ClientAccount, matAccountId, () => {
                this.toasterService.success('Mat account details updated successfully!', 'Success');
                this.dialogRef.close({ event: MatDialogCloseEvent.SUCCESS });
              });
            }
            else{
              this.toasterService.success('Mat account details updated successfully!', 'Success');
              this.dialogRef.close({ event: MatDialogCloseEvent.SUCCESS });
            }
          }
        }, (errorResponse: HttpErrorResponse) => {
          if(errorResponse.error){
            this.toasterService.error(errorResponse.error, "Error");
          }
        })
  }

  getAccountAdminDetails() {
    let payload: UserRoles = {} as UserRoles;
    payload.userRole = this.userRole;
    this.matAccountService.getAccountAdminDetails(this.userRole).subscribe((response: Users[] | any) => {
      this.accountAdminList = response;
      this.patchAccountDetail();
    },(err: HttpErrorResponse)=>{
      if(err.error){
        this.toasterService.error(err.error, "Error");
      }
    });
  }

  cancel() {
    this.dialogRef.close({ event: MatDialogCloseEvent.CANCELLED });
  }

  uploadDocument(formData: FormData, documentTypeId: string, referenceId: string, callback?: () => void) {
    this.matAccountService.uploadDocument(formData, documentTypeId, referenceId).subscribe(
      (res: any) => {
        if (res) {
          console.log("Uploaded Successfully");
          if (callback) {
            callback();
          }
        }
      },
      (error: HttpErrorResponse) => {
        if(error.error){
          this.toasterService.error(error.error, "Error");
        }
        if (callback) {
          callback();
        }
      }
    );
  }

  uploadFromLocal(event: Event, triggeredManually = false): void {
    if (!triggeredManually) {
      event.preventDefault();
      this.sharedService.setUploadedFile(this.matAccountUploadFileList);
      this.dialog.open(ConfirmDialogComponent, { width: '675px', data: {
        hideUpload: false,
        isParentUpload: true
      }}).afterClosed().subscribe(() => {
        this.selectedFileCount = this.matAccountUploadFileList.length;
      });
    }
    if (this.editMatFileInput) {
      this.editMatFileInput.nativeElement.click();
    }
  }

  onFileSelected(event: any) {
    if (!this.matAccountUploadFileList) {
      this.matAccountUploadFileList = [];
    }

    let filesExist = event.target.files.length;
    if (filesExist > 0) {
      for (let i = 0; i < filesExist; i++) {
        if (event.target.files && event.target.files[i]) {
          if(event.target.files[i].size <= this.maxSize){
            let _object: File = event.target.files[i];

            this.matAccountUploadFileList.push(_object);
          }
          else{
            this.toasterService.warning('File size must be less than 1 MB.');
          }
        }
      }
      this.selectedFileCount = this.matAccountUploadFileList.length;
      this.sharedService.changeUploadingStatus(false);
      this.sharedService.setUploadedFile(this.matAccountUploadFileList);
      this.editMatFileInput!.nativeElement.value = null;
    }
  }
}
